import cx from 'classnames'
import { useState, useRef } from 'react'

import Styles from './SelectedPlanForm.module.scss'

import {
  AutopilotPlan,
  RecurringStripePrice,
  StripePrice,
  isRecurringPrice,
  recurringPrices,
} from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { nameMap, iconMap } from '../../../Plans/components/PlanDetails/PlanDetails'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Pressable } from '@react-aria/interactions'
import { Button } from 'blaze-ui/Button'
import { StripePaymentForm, StripePaymentFormRef } from 'components/StripePaymentForm/StripePaymentForm'
import { OnStartPlan } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { useRootStore } from 'stores/RootStore'
import { useSetupIntent } from 'components/TimeTriggerPaywall/screens/Checkout/Checkout'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { calculateExtraAccountCharges } from 'components/Settings/TieredBilling/utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { EnableAutoReload } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/components/EnableAutoReload/EnableAutoReload'
import { ArrowRight } from 'blaze-ui/icons'
import { PlanLengthSelector } from '../PlanLengthSelector/PlanLengthSelector'
import { useAddOnDisplayItems } from '../PlanLengthSelector/useAddOnDisplayItems'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { PromotionCode } from 'api/clients/StripePromotionCodeClient'

export const SelectedPlanForm = ({
  selectedPlan,
  currentPrice,
  initialSelectedPrice,
  onBack,
  onChangePlan,
  onStartPlan,
  shouldAutoReloadCredits,
  toggleAutoReloadCredits,
  isCheckoutStep: isCheckoutStepProp,
  setIsCheckoutStep: setIsCheckoutStepProp,
}: {
  selectedPlan: AutopilotPlan
  currentPrice: StripePrice | null
  initialSelectedPrice?: StripePrice
  onBack: () => void
  onChangePlan: () => void
  onStartPlan: OnStartPlan
  shouldAutoReloadCredits: boolean
  toggleAutoReloadCredits: () => void
  isCheckoutStep?: boolean
  setIsCheckoutStep?: (value: boolean) => void
}) => {
  const tierUsageStore = useRootStore().tierUsageStore
  const isVerticalPlanLength = useFlipperFlag('vertical_plan_length')
  const { currentSubscription, autopilotPlans = [], autopilotAddOns = [] } = tierUsageStore
  const extraAccountsAddOn =
    autopilotAddOns.find((addOn) => addOn.key === PaymentPlanKey.BlazeAutopilotExtraAccounts) ?? null
  // Extra accounts are only charged for what a plan's own allowance does not cover, so every
  // quote has to name the plan it is quoting rather than reusing the current charge (BLZ-16162).
  const selectedPlanExtraAccountOptions = { targetPlan: selectedPlan, extraAccountsAddOn }
  const hasScheduledCancellation = !!currentSubscription?.cancel_at && currentSubscription?.status === 'active'
  const [internalCheckoutStep, setInternalCheckoutStep] = useState(false)
  const isCheckoutStep =
    isVerticalPlanLength && setIsCheckoutStepProp ? isCheckoutStepProp ?? false : internalCheckoutStep
  const setCheckoutStep = setIsCheckoutStepProp ?? setInternalCheckoutStep

  // Plan lengths are subscription intervals, so one-time prices (e.g. credit packs) are not selectable.
  const selectablePrices = recurringPrices(selectedPlan.prices)

  const getDefaultPrice = (): RecurringStripePrice | null => {
    if (initialSelectedPrice && isCheckoutStepProp && isRecurringPrice(initialSelectedPrice)) {
      return initialSelectedPrice
    }
    if (hasScheduledCancellation && currentPrice && isRecurringPrice(currentPrice)) return currentPrice
    if (currentPrice?.product === selectedPlan.id && isRecurringPrice(currentPrice)) return currentPrice
    return selectablePrices[3] ?? null // 12-month plan as default
  }
  const defaultPrice = getDefaultPrice()
  const [selectedPrice, setSelectedPrice] = useState<RecurringStripePrice | null>(defaultPrice)
  const [isProcessing, setIsProcessing] = useState(false)
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { data: clientSecret, isLoading } = useSetupIntent(currentWorkspaceId)
  const stripeFormRef = useRef<StripePaymentFormRef>(null)
  const [appliedPromoCode, setAppliedPromoCode] = useState<PromotionCode | undefined>()
  const [couponParams, setCouponParams] = useState<{
    amount_off?: number
    percent_off?: number
    duration?: string
    duration_in_months?: number
  }>({})

  const Icon = iconMap[selectedPlan.key]
  const addOnDisplayItems = useAddOnDisplayItems(selectedPlan)
  const currentPriceIsSelected =
    selectedPrice?.id === currentPrice?.id &&
    currentSubscription?.subscription_payment_method !== null &&
    currentSubscription?.status === 'active' &&
    !currentSubscription?.cancel_at

  const getCtaButtonText = () => {
    if (isVerticalPlanLength && isCheckoutStep) return 'Purchase'

    if (!hasScheduledCancellation || !selectedPrice || !currentPrice) {
      return 'Pay Now'
    }

    const selectedTotalUpfront =
      selectedPrice.unit_amount / 100 +
      calculateExtraAccountCharges(
        currentSubscription,
        selectedPrice.recurring.interval_count,
        selectedPlanExtraAccountOptions,
      )

    const currentPlan = autopilotPlans.find((plan) => plan.key === currentSubscription?.plan)
    const currentTotalUpfront =
      currentPrice.unit_amount / 100 +
      calculateExtraAccountCharges(currentSubscription, currentPrice.recurring?.interval_count ?? 1, {
        targetPlan: currentPlan,
        extraAccountsAddOn,
      })

    // Same or less expensive plan → Reactivate (no additional charge now, credits applied)
    // More expensive plan → Pay Now (additional charge required)
    return selectedTotalUpfront <= currentTotalUpfront ? 'Reactivate' : 'Pay Now'
  }

  const handlePaymentMethodCreated = async (paymentMethodId?: string) => {
    if (!selectedPrice) return

    try {
      await onStartPlan({
        paymentMethodId,
        chosenPriceId: selectedPrice.id,
        promotionCode: appliedPromoCode?.id,
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start plan'
      showToastMessage(errorMessage, 'error')
      setIsProcessing(false)
    }
  }

  const handleError = (error: string) => {
    setIsProcessing(false)
  }

  const handleStartPlan = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (stripeFormRef.current) {
      setIsProcessing(true)
      await stripeFormRef.current.submitPayment()
    }
  }

  return (
    <div className={Styles.container} data-vertical-plan-length={isVerticalPlanLength ? 'on' : 'off'}>
      {!isVerticalPlanLength && (
        <>
          <div className={cx(Styles.chosenPlan, Styles[selectedPlan.key])}>
            <Icon color="var(--light-100)" />
            <Heading level={4} color="var(--light-100)">
              You selected the {nameMap[selectedPlan.key]} plan
            </Heading>
            <Button
              variant="secondary-bg-agnostic"
              size="sm"
              onPress={onChangePlan}
              className={Styles.changePlanButton}
            >
              Change Plan
            </Button>
          </div>
          <Text>The longer Autopilot runs, the more you grow and the more you save.</Text>
        </>
      )}
      {isVerticalPlanLength && !isCheckoutStep && (
        <PlanLengthSelector
          selectedPlan={selectedPlan}
          selectedPrice={selectedPrice}
          onSelectPrice={setSelectedPrice}
        />
      )}
      {!isVerticalPlanLength && (
        <div className={Styles.pricesContainer}>
          {selectablePrices.map((price) => {
            const extraAccountCharges = calculateExtraAccountCharges(
              currentSubscription,
              price.recurring.interval_count,
              selectedPlanExtraAccountOptions,
            )
            const basePriceMonthly = Math.ceil(price.unit_amount / 100 / price.recurring.interval_count)
            const extraAccountMonthly = Math.ceil(extraAccountCharges / price.recurring.interval_count)
            const totalMonthly = basePriceMonthly + extraAccountMonthly
            const totalPrice = price.unit_amount / 100 + extraAccountCharges

            return (
              <Pressable onPress={() => setSelectedPrice(price)} key={price.id}>
                <div className={cx(Styles.price, { [Styles.selected]: price.id === selectedPrice?.id })}>
                  <Text variant="smallList">
                    {price.recurring.interval_count === 1 ? 'Monthly' : `${price.recurring.interval_count} months`}
                  </Text>
                  <div className={Styles.priceAmount}>
                    <Heading level={4}>${totalMonthly}</Heading>
                    <Text variant="placeholder">/mo</Text>
                  </div>
                  {price.recurring.interval_count > 1 && (
                    <Text variant="metadata" color="var(--dark-40)">
                      ${totalPrice.toLocaleString()} total
                    </Text>
                  )}
                  {price.metadata.percent_off && (
                    <span className={Styles.percentOffPill}>{price.metadata.percent_off}% off</span>
                  )}
                </div>
              </Pressable>
            )
          })}
        </div>
      )}
      {!isVerticalPlanLength && (
        <EnableAutoReload
          shouldAutoReloadCredits={shouldAutoReloadCredits}
          toggleAutoReloadCredits={toggleAutoReloadCredits}
          name="selected-plan-enable-auto-reload-credits"
          variant="multi-line"
        />
      )}
      {selectedPrice && !isCheckoutStep && isVerticalPlanLength && (
        <div className={Styles.buttonContainer}>
          <Button variant="tertiary" size="lg" onPress={onBack}>
            Back
          </Button>
          <Button
            isDisabled={isProcessing || isLoading || !clientSecret || currentPriceIsSelected}
            size="lg"
            onPress={() => setCheckoutStep(true)}
          >
            <span className={Styles.ctaButtonText}>
              Proceed to Checkout
              <ArrowRight size={16} />
            </span>
          </Button>
        </div>
      )}
      {selectedPrice && (!isVerticalPlanLength || isCheckoutStep) && (
        <>
          {isVerticalPlanLength &&
            isCheckoutStep &&
            (() => {
              const intervalCount = selectedPrice.recurring.interval_count
              const basePlanTotal = Math.round(selectedPrice.unit_amount / 100)
              const basePlanMonthly = Math.ceil(selectedPrice.unit_amount / intervalCount / 100)
              const addonsTotalUpfront = addOnDisplayItems.reduce(
                (acc, item) => acc + Math.round(item.monthlyPriceTotal * intervalCount),
                0,
              )
              const grandTotal = basePlanTotal + addonsTotalUpfront
              const isGrandTotalDifferentThanBasePlanTotal = grandTotal !== basePlanTotal
              const planLabel =
                intervalCount === 1
                  ? `${nameMap[selectedPlan.key]} Monthly plan`
                  : `${nameMap[selectedPlan.key]} ${intervalCount} month plan`

              return (
                <>
                  <div className={Styles.checkoutSummary}>
                    <div className={Styles.checkoutSummaryHeader}>
                      <Heading level={3}>{planLabel}</Heading>
                      <Heading level={3}>Total: ${grandTotal.toLocaleString()}</Heading>
                    </div>
                    <div className={Styles.checkoutLineItems}>
                      <div className={Styles.checkoutLineItem}>
                        <Text>Base plan</Text>
                        <div className={Styles.checkoutLineItemRight}>
                          <Text color="var(--dark-40)" variant="secondary">
                            ${basePlanMonthly}/mo
                            {selectedPrice.metadata.percent_off && ` (${selectedPrice.metadata.percent_off}% off)`}
                          </Text>
                          {isGrandTotalDifferentThanBasePlanTotal && (
                            <Text variant="primary">${basePlanTotal.toLocaleString()}</Text>
                          )}
                        </div>
                      </div>
                      {addOnDisplayItems.map((item) => (
                        <div key={item.key} className={Styles.checkoutLineItem}>
                          <Text>+ Add-on: {item.name}</Text>
                          <div className={Styles.checkoutLineItemRight}>
                            <Text variant="secondary" color="var(--dark-40)">
                              ${item.monthlyPriceForSingleAccount}/mo
                              {item.key === PaymentPlanKey.BlazeAutopilotExtraAccounts ? ` per account` : ''}
                            </Text>
                            <Text>${(item.monthlyPriceTotal * intervalCount).toLocaleString()}</Text>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={Styles.checkoutAutoReloadWrapper}>
                    <EnableAutoReload
                      shouldAutoReloadCredits={shouldAutoReloadCredits}
                      toggleAutoReloadCredits={toggleAutoReloadCredits}
                      name="selected-plan-enable-auto-reload-credits"
                      className={Styles.checkoutAutoReloadFullWidth}
                    />
                  </div>
                </>
              )
            })()}
          <form onSubmit={handleStartPlan}>
            <div className={Styles.creditCardForm}>
              <StripePaymentForm
                ref={stripeFormRef}
                clientSecret={clientSecret}
                onPaymentMethodCreated={handlePaymentMethodCreated}
                onError={handleError}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                disabled={currentPriceIsSelected}
                showPromotionCode
                onPromotionCodeChange={setAppliedPromoCode}
                couponParams={couponParams}
                onCouponApplied={(params) => setCouponParams(params || {})}
              />
            </div>

            <div className={Styles.buttonContainer}>
              <div className={Styles.rightButtonsContainer}>
                {!isVerticalPlanLength && (
                  <Text variant="placeholder" color="var(--dark-80)">
                    {`$${
                      selectedPrice.unit_amount / 100 +
                      calculateExtraAccountCharges(
                        currentSubscription,
                        selectedPrice.recurring.interval_count,
                        selectedPlanExtraAccountOptions,
                      )
                    } `}
                    {selectedPrice.recurring.interval_count > 1
                      ? `every ${selectedPrice.recurring.interval_count} months`
                      : 'monthly'}
                    {Object.keys(couponParams).length > 0 && ' after coupon'}
                  </Text>
                )}
                <Button
                  size="lg"
                  type="submit"
                  isDisabled={isProcessing || isLoading || !clientSecret || currentPriceIsSelected}
                >
                  <span className={Styles.ctaButtonText}>{getCtaButtonText()}</span>
                </Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  )
}
